"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Card, CardBody, CardHeader, Badge, Button, Spinner, Input, FormGroup, Label, Row, Col } from "reactstrap"
import { FaEye, FaCreditCard, FaSearch, FaExclamationTriangle, FaBell } from "react-icons/fa"
import DataTable from "react-data-table-component"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { PrintOrder } from "./badge-request"
import { toast } from "react-toastify"

interface Props {
  onViewOrder: (order: PrintOrder) => void
  onPayOrder: (order: PrintOrder) => void
  refresh: boolean
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

const UserOrdersList: React.FC<Props> = ({ onViewOrder, onPayOrder, refresh }) => {
  const [orders, setOrders] = useState<PrintOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)

  // Debounced search function
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])
    return debouncedValue
  }

  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const fetchOrders = useCallback(async (page = 1, pageSize = 10, search = "", status = "all") => {
    try {
      setLoading(true)
      const params = {
        page,
        page_size: pageSize,
        ...(search.trim() && { search: search.trim() }),
        ...(status !== "all" && { status }),
      }
      const response: PaginatedResponse<PrintOrder> = await BadgeRequestService.getUserOrders(params)
      setOrders(response.results)
      setTotalRows(response.count)
    } catch (error) {
      console.error("Failed to fetch user orders:", error)
      toast.error("Failed to load orders")
      setOrders([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [])

  // Effect for initial load and refresh
  useEffect(() => {
    fetchOrders(currentPage, perPage, debouncedSearchTerm, statusFilter)
  }, [refresh, currentPage, perPage, debouncedSearchTerm, statusFilter, fetchOrders])

  // Reset to first page when search or filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1)
    } else {
      fetchOrders(1, perPage, debouncedSearchTerm, statusFilter)
    }
  }, [debouncedSearchTerm, statusFilter])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setCurrentPage(page)
  }

  // Calculate total amount from order details
  const calculateTotalAmount = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  // Check if order needs payment
  const needsPayment = (order: PrintOrder): boolean => {
    const totalAmount = calculateTotalAmount(order)
    return order.status === "pending" && !order.is_postpaid && totalAmount > 0
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "paid":
        return "info"
      case "approved":
        return "success"
      case "printed":
        return "primary"
      case "cancelled":
        return "danger"
      default:
        return "secondary"
    }
  }

  const columns = [
    {
      name: "Order #",
      selector: (row: PrintOrder) => row.order_number,
      sortable: true,
      width: "180px",
      cell: (row: PrintOrder) => (
        <div className="position-relative">
          <div>
            <strong className="text-primary">{row.order_number}</strong>
            {row.is_postpaid && (
              <div>
                <Badge color="info" size="sm">
                  Postpaid
                </Badge>
              </div>
            )}
          </div>
          {/* Payment Required Indicator */}
          {needsPayment(row) && (
            <div className="position-absolute top-0 end-0" style={{ transform: "translate(50%, -50%)" }}>
              <div className="payment-indicator">
                <FaExclamationTriangle className="text-danger" size={16} title="Payment Required" />
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Company",
      selector: (row: PrintOrder) => row.participation_details?.exhibitor_name || "",
      sortable: true,
      width: "180px",
    },
    {
      name: "Exhibition",
      selector: (row: PrintOrder) => row.participation_details?.exhibition_title || "",
      sortable: true,
      width: "180px",
    },
    {
      name: "Participants",
      selector: (row: PrintOrder) => row.order_details?.length || 0,
      sortable: true,
      width: "120px",
      cell: (row: PrintOrder) => (
        <Badge color="secondary" pill>
          {row.order_details?.length || 0}
        </Badge>
      ),
    },
    {
      name: "Amount",
      selector: (row: PrintOrder) => calculateTotalAmount(row),
      sortable: true,
      width: "140px",
      cell: (row: PrintOrder) => {
        const totalAmount = calculateTotalAmount(row)
        const paymentRequired = needsPayment(row)
        return (
          <div className="position-relative">
            <div className={paymentRequired ? "payment-amount-highlight" : ""}>
              <strong className={paymentRequired ? "text-danger fw-bold" : ""}>
                RWF {totalAmount.toLocaleString()}
              </strong>
              {totalAmount === 0 && (
                <div>
                  <Badge color="success" size="sm">
                    FREE
                  </Badge>
                </div>
              )}
              {paymentRequired && (
                <div>
                  <Badge color="danger" size="sm" className="blink-badge">
                    <FaBell size={8} className="me-1" />
                    PAYMENT DUE
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      name: "Status",
      selector: (row: PrintOrder) => row.status,
      sortable: true,
      width: "120px",
      cell: (row: PrintOrder) => {
        const paymentRequired = needsPayment(row)
        return (
          <div className="position-relative">
            <Badge color={getStatusColor(row.status)} pill className={paymentRequired ? "status-with-payment" : ""}>
              {row.status.toUpperCase()}
            </Badge>
            {paymentRequired && (
              <div className="position-absolute top-0 start-100" style={{ transform: "translate(-50%, -50%)" }}>
                <div className="payment-pulse">
                  <FaCreditCard className="text-warning" size={12} />
                </div>
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: "Created",
      selector: (row: PrintOrder) => row.created_at,
      sortable: true,
      width: "120px",
      cell: (row: PrintOrder) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      name: "Actions",
      width: "140px",
      cell: (row: PrintOrder) => {
        const paymentRequired = needsPayment(row)
        return (
          <div className="d-flex gap-1">
            <Button color="primary" size="xs" onClick={() => onViewOrder(row)} title="View Details">
              <FaEye size={10} />
            </Button>
            {paymentRequired && (
              <Button
                color="danger"
                size="xs"
                onClick={() => onPayOrder(row)}
                title="Make Payment"
                className="payment-button-pulse"
              >
                <FaCreditCard size={10} />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
    },
  }

  // Count orders that need payment
  const paymentRequiredCount = orders.filter(needsPayment).length

  return (
    <>
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0">Orders</h5>
              {paymentRequiredCount > 0 && (
                <Badge color="danger" pill className="payment-alert-badge">
                  <FaExclamationTriangle size={10} className="me-1" />
                  {paymentRequiredCount} Payment{paymentRequiredCount > 1 ? "s" : ""} Required
                </Badge>
              )}
            </div>
            <Badge color="info" pill>
              {totalRows} total orders
            </Badge>
          </div>
        </CardHeader>
        <CardBody>
          {/* Filters */}
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label>Search</Label>
                <div className="position-relative">
                  <Input
                    type="text"
                    placeholder="Search by order number, company, or exhibition..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5"
                  />
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                </div>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Status</Label>
                <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="approved">Approved</option>
                  <option value="printed">Printed</option>
                  <option value="cancelled">Cancelled</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* DataTable with Server-side Pagination */}
          <DataTable
            columns={columns}
            data={orders}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            progressPending={loading}
            progressComponent={
              <div className="d-flex justify-content-center align-items-center py-4">
                <Spinner color="primary" />
              </div>
            }
            noDataComponent={
              <div className="text-center py-4">
                <div className="text-muted">
                  {searchTerm || statusFilter !== "all" ? "No orders match your filters" : "No orders found"}
                </div>
              </div>
            }
            customStyles={customStyles}
            onRowClicked={onViewOrder}
            highlightOnHover
            pointerOnHover
          />
        </CardBody>
      </Card>

      <style>{`
        .payment-indicator {
          background: white;
          border-radius: 50%;
          padding: 2px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          animation: bounce 2s infinite;
        }

        .payment-pulse {
          background: white;
          border-radius: 50%;
          padding: 2px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          animation: pulse 2s infinite;
        }

        .payment-amount-highlight {
          background: rgba(220, 53, 69, 0.1);
          border-radius: 4px;
          padding: 2px 4px;
          border-left: 3px solid #dc3545;
        }

        .blink-badge {
          animation: blink 2s infinite;
        }

        .payment-button-pulse {
          animation: buttonPulse 2s infinite;
          box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
        }

        .payment-alert-badge {
          animation: alertPulse 2s infinite;
        }

        .status-with-payment {
          border: 2px solid #ffc107 !important;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-5px);
          }
          60% {
            transform: translateY(-3px);
          }
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes blink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0.5;
          }
        }

        @keyframes buttonPulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
          }
          70% {
            transform: scale(1.05);
            box-shadow: 0 0 0 4px rgba(220, 53, 69, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
          }
        }

        @keyframes alertPulse {
          0% {
            background-color: #dc3545;
          }
          50% {
            background-color: #bb2d3b;
          }
          100% {
            background-color: #dc3545;
          }
        }
      `}</style>
    </>
  )
}

export default UserOrdersList
